import type { MembershipSummary, OwedBalance, OwnerPaymentRequest } from "@teamna/shared";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Button, Checkbox, DataTable, ErrorState, Notice, Panel, SkeletonList } from "../../components/index.ts";
import { useActiveBusiness, useOwnerPayments } from "@teamna/web-client/hooks/use-owner.ts";
import { formatDate, formatMoney } from "@teamna/web-client/lib/format.ts";
import { capital, type Words } from "../families/model.ts";
import { paymentColumns } from "../payments.tsx";
import { CardPaymentsComingSoon } from "../card-payments-coming-soon.tsx";
import { CancelRequestPanel, PaymentRequestList, RequestPaymentPanel, type PayLinkItem } from "./panels.tsx";
import { RecordPaymentButton, RecordPaymentPanel } from "./record-payment.tsx";

/** What the family owes, read once on the page and shared by the header, Overview and Payments. */
export type FamilyMoneyFacts = {
  owed: OwedBalance[];
  requests: OwnerPaymentRequest[];
  loading: boolean;
  error: unknown;
  /** Everything to pay that has a link: owed packages first, then open requests. */
  payable: PayLinkItem[];
  cardPayments: boolean;
  /** Teamna has card payments off for all businesses: say "Coming soon", not "connect Stripe". */
  cardPaymentsComingSoon?: boolean;
  /** Reads the profile again, before Record a payment chooses its first step. */
  refresh: () => Promise<{ isError: boolean }>;
  /** A participant of the family account: an owed package is paid through it also when no child is listed. */
  accountParticipantId?: string | null;
  /** A membership month that waits for a payment, read from the profile of that client. */
  renewal?: { participantId: string; name: string; membership: MembershipSummary } | null;
};

/** Owed packages and open requests that a payment link can pay. */
export function payableItems(owed: OwedBalance[], requests: OwnerPaymentRequest[]): PayLinkItem[] {
  return [
    ...owed.flatMap((row) => (row.payLink ? [{ key: row.purchaseId, what: row.packageName, amountFils: row.amountFils, url: row.payLink }] : [])),
    ...requests.flatMap((row) =>
      (row.status === "pending" || row.status === "overdue") && row.payLink
        ? [{ key: row.id, what: row.description, amountFils: row.amountFils, url: row.payLink }]
        : [],
    ),
  ];
}

type Action = "request";

/** A child as the money actions need it: the participant id and the name. Read from the family's own list. */
export type FamilyPerson = { id: string; displayName: string; groupId?: string | null };

/**
 * The family's money. On the Overview it is only the "To pay" box (`summary`). On the Payments
 * tab it is the whole of it: the actions (record a payment, ask for an amount), "To pay" with the
 * payment link, the requests, and what the family paid.
 *
 * A request is for one child: with two or more children, the owner chooses the child first. Record
 * a payment asks what the payment is for, an owed package or a new package for one of the children
 * (`record-payment.tsx`).
 */
export function FamilyMoney({
  businessId,
  people: children,
  memberName,
  businessName,
  timezone,
  money,
  words,
  ownerOnly,
  adultIds,
  summary = false,
  onSendLink,
}: {
  businessId: string;
  people: FamilyPerson[];
  memberName: string;
  businessName: string;
  timezone: string;
  money: FamilyMoneyFacts;
  words: Words;
  ownerOnly: boolean;
  /** The family's adults: a payment by any of them is the family's. */
  adultIds: string[];
  summary?: boolean;
  onSendLink: (items: PayLinkItem[]) => void;
}) {
  const navigate = useNavigate();
  // The front desk (T1) sells a package and records a payment; a request and card payments are the owner's.
  const { may } = useActiveBusiness();
  const [pending, setPending] = useState<Action | null>(null);
  const [action, setAction] = useState<{ kind: Action; participant: FamilyPerson } | null>(null);
  const [recording, setRecording] = useState(false);
  const [cancelling, setCancelling] = useState<OwnerPaymentRequest | null>(null);
  const [left, setLeft] = useState<ReadonlySet<string>>(new Set());
  const first = children[0] ?? null;

  function start(kind: Action) {
    if (children.length === 1 && first) setAction({ kind, participant: first });
    else setPending(kind);
  }

  const chosen = money.payable.filter((item) => !left.has(item.key));
  const chosenFils = chosen.reduce((total, item) => total + item.amountFils, 0);
  const openRequests = money.requests.filter((row) => row.status === "pending" || row.status === "overdue");
  const toPayCount = money.owed.length + openRequests.length;

  const toPay = (
    <section aria-label="To pay" className="flex flex-col gap-2">
      <h3 className="text-[16px]">To pay</h3>
      {money.loading ? (
        <SkeletonList rows={2} />
      ) : money.error ? (
        <ErrorState error={money.error} />
      ) : toPayCount === 0 ? (
        <p className="text-[14px] text-ink-3">{memberName} has nothing to pay.</p>
      ) : (
        <div className="rounded-card border border-line bg-paper">
          <ul className="divide-y divide-line">
            {money.owed.map((row) => (
              <PayRow
                key={row.purchaseId}
                itemKey={row.purchaseId}
                what={row.packageName}
                detail={row.renewsOn ? `Package, for the period from ${formatDate(`${row.renewsOn}T12:00:00.000Z`, timezone)}` : "Package"}
                amountFils={row.amountFils}
                linked={Boolean(row.payLink) && money.cardPayments}
                left={left}
                onLeft={setLeft}
              />
            ))}
            {openRequests.map((row) => (
              <PayRow
                key={row.id}
                itemKey={row.id}
                what={row.description}
                detail={`${row.participantName ? `For ${row.participantName}. ` : ""}Pay by ${formatDate(`${row.dueOn}T12:00:00.000Z`, timezone)}`}
                amountFils={row.amountFils}
                linked={Boolean(row.payLink) && money.cardPayments}
                left={left}
                onLeft={setLeft}
              />
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-2 rounded-b-card border-t border-line bg-surface px-4 py-3">
            <span className="min-w-0 flex-1 text-[14px] text-ink-2">
              {money.cardPayments ? (
                <>
                  Chosen: <span className="font-semibold text-ink tabular-nums">{formatMoney(chosenFils)}</span>
                </>
              ) : (
                money.cardPaymentsComingSoon
                  ? "Payment links are coming soon."
                  : "Card payments are off, so there is no payment link."
              )}
            </span>
            {money.cardPayments ? (
              <Button disabled={chosen.length === 0} onClick={() => onSendLink(chosen)}>
                Send a payment link
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );

  const panels = (
    <>
      {pending ? (
        <Panel
          open
          onClose={() => setPending(null)}
          title={`For which ${words.participant}?`}
          actions={
            <Button variant="secondary" onClick={() => setPending(null)}>
              Cancel
            </Button>
          }
        >
          <div className="flex flex-col gap-2">
            {children.map((participant) => (
              <Button
                key={participant.id}
                variant="secondary"
                onClick={() => {
                  setAction({ kind: pending, participant });
                  setPending(null);
                }}
              >
                {participant.displayName}
              </Button>
            ))}
          </div>
        </Panel>
      ) : null}
      {recording ? (
        <RecordPaymentPanel
          businessId={businessId}
          people={children}
          memberName={memberName}
          owed={money.owed}
          renewal={money.renewal ?? null}
          accountParticipantId={money.accountParticipantId ?? null}
          timezone={timezone}
          onClose={() => setRecording(false)}
        />
      ) : null}
      {action?.kind === "request" ? (
        <RequestPaymentPanel
          businessId={businessId}
          participantId={action.participant.id}
          name={action.participant.displayName}
          memberName={memberName}
          businessName={businessName}
          cardPayments={money.cardPayments}
          comingSoon={money.cardPaymentsComingSoon === true}
          onClose={() => setAction(null)}
        />
      ) : null}
      {cancelling ? (
        <CancelRequestPanel
          businessId={businessId}
          participantId={cancelling.participantId ?? first?.id ?? ""}
          request={cancelling}
          memberName={memberName}
          onClose={() => setCancelling(null)}
        />
      ) : null}
    </>
  );

  if (summary) {
    return (
      <>
        {toPay}
        {panels}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {first || money.owed.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          <RecordPaymentButton
            disabled={money.loading || Boolean(money.error)}
            refresh={money.refresh}
            onClick={() => setRecording(true)}
          />
          {/* A request is paid by card, so while card payments are coming soon there is nothing to ask with. */}
          {!first || money.cardPaymentsComingSoon || !may("requestPayment") ? null : (
            <Button variant="secondary" onClick={() => start("request")}>
              Ask for an amount
            </Button>
          )}
        </div>
      ) : (
        <p className="text-[14px] text-ink-2">
          Add a {words.participant} in Settings first. A package and a request are for one {words.participant}.
        </p>
      )}
      {money.cardPaymentsComingSoon ? (
        <CardPaymentsComingSoon />
      ) : !money.cardPayments && may("setUpCardPayments") ? (
        <Notice
          tone="info"
          title="Card payments are off"
          action={
            <div>
              <Button variant="secondary" onClick={() => void navigate({ to: "/owner/setup/get-paid" })}>
                Get paid by card
              </Button>
            </div>
          }
        >
          {capital(words.members)} pay in cash or by bank transfer, and you record it here. Connect to Stripe to send payment links.
        </Notice>
      ) : null}
      {toPay}
      {money.requests.length > 0 ? (
        <PaymentRequestList
          requests={money.requests}
          timezone={timezone}
          onLink={(request) =>
            request.payLink ? onSendLink([{ key: request.id, what: request.description, amountFils: request.amountFils, url: request.payLink }]) : undefined
          }
          {...(may("cancelPaymentRequest") ? { onCancel: setCancelling } : {})}
        />
      ) : null}
      <PaidHistory businessId={businessId} adultIds={adultIds} timezone={timezone} memberName={memberName} ownerOnly={ownerOnly} words={words} />
      {panels}
    </div>
  );
}

function PayRow({
  itemKey,
  what,
  detail,
  amountFils,
  linked,
  left,
  onLeft,
}: {
  itemKey: string;
  what: string;
  detail: string;
  amountFils: number;
  /** Only an item with a link can go in a payment link. */
  linked: boolean;
  left: ReadonlySet<string>;
  onLeft: (next: ReadonlySet<string>) => void;
}) {
  const body = (
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="font-semibold text-ink" dir="auto">
        {what}
      </span>
      <span className="text-[13px] text-ink-2">{detail}</span>
    </span>
  );
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      {linked ? (
        <Checkbox
          label={`Put ${what} in the payment link`}
          hideLabel
          checked={!left.has(itemKey)}
          onChange={(next) => {
            const copy = new Set(left);
            if (next) copy.delete(itemKey);
            else copy.add(itemKey);
            onLeft(copy);
          }}
        />
      ) : null}
      {body}
      <span className="shrink-0 font-semibold tabular-nums">{formatMoney(amountFils)}</span>
    </li>
  );
}

/**
 * What the family paid: card payments, and the cash and bank transfers recorded for them. Read from
 * the payments register (its newest 200), so it is the owner's only, like the Payments page.
 */
function PaidHistory({
  businessId,
  adultIds,
  timezone,
  memberName,
  ownerOnly,
  words,
}: {
  businessId: string;
  adultIds: string[];
  timezone: string;
  memberName: string;
  ownerOnly: boolean;
  words: Words;
}) {
  const payments = useOwnerPayments(businessId, ownerOnly);
  const rows = useMemo(() => {
    const ids = new Set(adultIds);
    return (payments.data?.items ?? []).filter((row) => ids.has(row.userId));
  }, [payments.data, adultIds]);

  return (
    <section aria-label="Paid" className="flex flex-col gap-2">
      <h3 className="text-[16px]">Paid</h3>
      {!ownerOnly ? (
        <p className="text-[14px] text-ink-2">Payments are visible to the business owner.</p>
      ) : payments.error && !payments.data ? (
        <ErrorState error={payments.error} onRetry={() => void payments.refetch()} />
      ) : (
        <DataTable
          caption={`Payments by ${memberName}`}
          columns={paymentColumns(timezone, capital(words.member), { withPayer: false })}
          rows={rows}
          rowKey={(row) => row.id}
          isLoading={payments.isLoading}
          defaultSort={{ id: "date", direction: "desc" }}
          empty={{ title: "No payments yet", message: `Card payments, cash and bank transfers of ${memberName} show here.` }}
          countLabel={(count) => `${count} ${count === 1 ? "payment" : "payments"}`}
          density="compact"
        />
      )}
    </section>
  );
}
